<template>
  <v-container class="entity-detail-page">
    <entity-detail-header
      :title="partner.name || 'Parceiro'"
      :subtitle="documentLabel"
      back-label="Voltar para parceiros"
      @back="handleBack"
    >
      <template #badges>
        <status-badge-vue :status="partner.status" />
        <span
          v-if="partner.status === 'ACTIVE'"
          :class="['premium-badge', partnerPremiumBadgeClass(partner.isPremium, partner.premiumSource)]"
        >
          {{ formatPartnerPremiumLabel(partner.isPremium, partner.premiumSource) }}
        </span>
        <v-chip v-if="partner.category.name" small outlined color="primary">
          {{ partner.category.name }}
        </v-chip>
      </template>
      <template #actions>
        <v-btn
          v-if="partner.phone"
          class="whatsapp-btn"
          depressed
          @click="openWhatsApp(partner.phone, partner.name)"
        >
          <v-icon left small>mdi-whatsapp</v-icon>
          WhatsApp
        </v-btn>
        <v-btn
          v-if="partner.status === 'ACTIVE'"
          depressed
          color="#E5183D"
          class="white--text"
          :disabled="!partner.document"
          @click="handlePushRouteQRCODE"
        >
          <v-icon left small>mdi-qrcode</v-icon>
          QR Code estático
        </v-btn>
        <template v-if="canReview">
          <button-vue dark color="red" type="repprove" @click="handleReprove">
            Reprovar
          </button-vue>
          <button-vue dark color="green" type="primary" @click="handleAprove">
            Aprovar
          </button-vue>
        </template>
      </template>
    </entity-detail-header>

    <v-row v-if="loading">
      <v-col cols="12" lg="8">
        <v-skeleton-loader type="card" class="mb-4" />
        <v-skeleton-loader type="card" class="mb-4" />
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col cols="12" lg="4">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12" lg="8">
        <entity-detail-section title="Dados principais" icon="mdi-store-outline">
          <v-row>
            <v-col cols="12" md="6">
              <title-subtitle-vue
                :label="partner.typeCompany === 'PJ' ? 'Razão social' : 'Nome completo'"
                :value="partner.name || '—'"
              />
            </v-col>
            <v-col cols="12" md="6">
              <title-subtitle-vue
                :label="partner.typeCompany === 'PJ' ? 'Nome fantasia' : 'Como quer ser chamado'"
                :value="partner.nickname || partner.name || '—'"
              />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue
                :label="partner.typeCompany === 'PJ' ? 'CNPJ' : 'CPF'"
                :value="formatDocument(partner.document)"
              />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="Email" :value="partner.email || '—'" />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="Telefone" :value="formatPhone(partner.phone)" />
            </v-col>
          </v-row>
        </entity-detail-section>

        <entity-detail-section title="Endereço" icon="mdi-map-marker-outline">
          <v-row>
            <v-col cols="12" md="6">
              <title-subtitle-vue label="Logradouro" :value="partner.address.street || '—'" />
            </v-col>
            <v-col cols="12" md="2">
              <title-subtitle-vue label="Número" :value="partner.address.addressNumber || '—'" />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="Bairro" :value="partner.address.neighborhood || '—'" />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="Complemento" :value="partner.address.complement || '—'" />
            </v-col>
            <v-col cols="12" md="3">
              <title-subtitle-vue label="CEP" :value="formatZipCod(partner.address.zipCode)" />
            </v-col>
            <v-col cols="12" md="3">
              <title-subtitle-vue label="Cidade" :value="partner.address.cityName || '—'" />
            </v-col>
            <v-col cols="12" md="2">
              <title-subtitle-vue label="Estado" :value="partner.address.state || '—'" />
            </v-col>
          </v-row>
        </entity-detail-section>

        <entity-detail-section title="Responsável / usuário" icon="mdi-account-tie-outline">
          <v-row>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="Nome" :value="partner.user.name || '—'" />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="Email" :value="partner.user.email || '—'" />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="Telefone" :value="formatPhone(partner.user.phone)" />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="CPF" :value="formatDocument(partner.user.document)" />
            </v-col>
          </v-row>
        </entity-detail-section>

        <entity-detail-section title="Dados bancários" icon="mdi-bank-outline">
          <v-row>
            <v-col cols="12" md="4">
              <title-subtitle-vue
                label="Conta bancária"
                :value="partner.digitalAccountNumber || '—'"
              />
            </v-col>
            <v-col cols="12" md="8">
              <title-subtitle-vue label="Chave PIX" :value="partner.pixkey || '—'" />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="Taxa administrativa" :value="`${partner.tax}%`" />
            </v-col>
          </v-row>
        </entity-detail-section>

        <entity-detail-section
          v-if="partner.businessHours.length"
          title="Horário de funcionamento"
          icon="mdi-clock-outline"
        >
          <div class="business-hours-table">
            <div class="business-hours-table__head">
              <span>Dia</span>
              <span>Abertura</span>
              <span>Fechamento</span>
            </div>
            <div
              v-for="(item, index) in partner.businessHours"
              :key="index"
              class="business-hours-table__row"
            >
              <span>{{ item.day }}</span>
              <span>{{ item.startTime }}</span>
              <span>{{ item.endTime }}</span>
            </div>
          </div>
        </entity-detail-section>

        <entity-detail-section
          v-if="partner.typeCompany === 'PJ'"
          title="Documentos da empresa"
          icon="mdi-file-document-outline"
        >
          <div class="doc-links">
            <v-btn
              v-if="partner.contractFile"
              outlined
              color="primary"
              class="doc-links__btn"
              @click="handlePdfClick(partner.contractFile)"
            >
              <v-icon left small>mdi-file-pdf-box</v-icon>
              Contrato social
            </v-btn>
            <v-btn
              v-if="partner.registrationStatusFile"
              outlined
              color="primary"
              class="doc-links__btn"
              @click="handlePdfClick(partner.registrationStatusFile)"
            >
              <v-icon left small>mdi-file-pdf-box</v-icon>
              Situação cadastral
            </v-btn>
          </div>
        </entity-detail-section>
      </v-col>

      <v-col cols="12" lg="4">
        <entity-detail-sidebar-card title="Resumo" icon="mdi-information-outline">
          <div class="sidebar-summary">
            <div class="sidebar-summary__row">
              <span class="sidebar-summary__label">Status</span>
              <status-badge-vue :status="partner.status" />
            </div>
            <div v-if="partner.status === 'ACTIVE'" class="sidebar-summary__row">
              <span class="sidebar-summary__label">Plano</span>
              <span
                :class="['premium-badge', 'premium-badge--compact', partnerPremiumBadgeClass(partner.isPremium, partner.premiumSource)]"
              >
                {{ formatPartnerPremiumLabel(partner.isPremium, partner.premiumSource) }}
              </span>
            </div>
            <div class="sidebar-summary__row">
              <span class="sidebar-summary__label">Tipo</span>
              <span class="sidebar-summary__value">{{ partner.typeCompany === 'PJ' ? 'Pessoa jurídica' : 'Pessoa física' }}</span>
            </div>
            <div class="sidebar-summary__row">
              <span class="sidebar-summary__label">Cadastro</span>
              <span class="sidebar-summary__value">{{ formatDate(partner.createdAt) }}</span>
            </div>
            <div class="sidebar-summary__row">
              <span class="sidebar-summary__label">Categoria</span>
              <span class="sidebar-summary__value">{{ partner.category.name || '—' }}</span>
            </div>
            <div class="sidebar-summary__row">
              <span class="sidebar-summary__label">Taxa</span>
              <span class="sidebar-summary__value">{{ partner.tax }}%</span>
            </div>
          </div>
        </entity-detail-sidebar-card>

        <entity-detail-sidebar-card
          v-if="partner.status === 'ACTIVE'"
          title="Ações rápidas"
          icon="mdi-lightning-bolt-outline"
        >
          <v-btn
            block
            depressed
            color="#E5183D"
            class="white--text mb-3"
            :disabled="!partner.document"
            @click="handlePushRouteQRCODE"
          >
            <v-icon left small>mdi-qrcode</v-icon>
            Gerar QR Code estático
          </v-btn>
          <v-btn
            v-if="partner.phone"
            block
            depressed
            class="whatsapp-btn"
            @click="openWhatsApp(partner.phone, partner.name)"
          >
            <v-icon left small>mdi-whatsapp</v-icon>
            Contato via WhatsApp
          </v-btn>
        </entity-detail-sidebar-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showPdfDialog" max-width="900">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center py-3">
          <span>Visualizar documento</span>
          <v-btn icon @click="showPdfDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <object :data="clickedPdf" type="application/pdf" width="100%" height="600px" />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import TitleSubtitleVue from "@/components/titleSubtitle/TitleSubtitle.vue";
import StatusBadgeVue from "@/components/statusBadge/StatusBadge.vue";
import ButtonVue from "@/components/button/Button.vue";
import EntityDetailHeader from "@/components/entityDetail/EntityDetailHeader.vue";
import EntityDetailSection from "@/components/entityDetail/EntityDetailSection.vue";
import EntityDetailSidebarCard from "@/components/entityDetail/EntityDetailSidebarCard.vue";
import { PARTNER_REVIEW_STATUSES } from "@/config/userStatusFilters";
import { GetPartner } from "@/services/partners/GetPartner";
import { UpdateOnboardingPartner } from "@/services/onboarding-partner/UpdateOnboarding";
import { PartnerDetailsType } from "@/types/partner/Partner";
import {
  formatPartnerPremiumLabel,
  partnerPremiumBadgeClass,
} from "@/utils/partnerPremium";
import { format } from "date-fns";

export default defineComponent({
  components: {
    TitleSubtitleVue,
    StatusBadgeVue,
    ButtonVue,
    EntityDetailHeader,
    EntityDetailSection,
    EntityDetailSidebarCard,
  },
  props: ["mode", "pClient"],
  computed: {
    canReview(): boolean {
      return PARTNER_REVIEW_STATUSES.has(this.partner.status);
    },
    documentLabel(): string {
      const doc = this.formatDocument(this.partner.document);
      const type = this.partner.typeCompany === "PJ" ? "CNPJ" : "CPF";
      return doc !== "—" ? `${type}: ${doc}` : "";
    },
  },
  data() {
    return {
      loading: true,
      showPdfDialog: false,
      clickedPdf: "",
      partner: {
        id: "",
        name: "",
        status: "PENDING",
        nickname: "",
        tax: 5,
        typeCompany: "PJ",
        category: { name: "", id: "" },
        phone: "",
        email: "",
        document: "",
        pixkey: "",
        cnaeCode: "",
        address: {
          state: "",
          street: "",
          addressNumber: "",
          neighborhood: "",
          complement: "",
          zipCode: "",
          cityName: "",
          ibge: "",
        },
        users: [],
        user: {
          name: "",
          email: "",
          phone: "",
          document: "",
          password: "",
          nickname: "",
          birthdate: "",
          motherName: "",
        },
        associate: [],
        businessHours: [],
        createdAt: "",
        digitalAccountNumber: "",
        digitalAccountDocument: "",
        associates: [],
        categoryId: "1",
        contractFile: "",
        registrationStatusFile: "",
      } as PartnerDetailsType,
    };
  },
  methods: {
    formatPartnerPremiumLabel,
    partnerPremiumBadgeClass,
    async handleLoadOnboarding() {
      this.loading = true;
      try {
        const getPartner = new GetPartner();
        const onboarding = await getPartner.execute(this.$route.params.id as string);
        this.partner.typeCompany = onboarding.document.length > 11 ? "PJ" : "PF";
        this.partner.id = onboarding.id || "";
        this.partner.name = onboarding.name || "";
        this.partner.nickname = onboarding.nickname || "";
        this.partner.document = onboarding.document || "";
        this.partner.cnaeCode = onboarding.cnaeCode || "";
        this.partner.category.name = onboarding.category.name || "";
        this.partner.tax = onboarding.tax || 0;
        this.partner.status = onboarding.status;
        this.partner.createdAt = onboarding.createdAt || "";
        this.partner.digitalAccountDocument = onboarding.digitalAccountDocument || "";
        this.partner.digitalAccountNumber = onboarding.digitalAccountNumber || "";
        this.partner.pixkey = onboarding.pixkey || "";
        this.partner.phone = onboarding.phone || "";
        this.partner.email = onboarding.email || "";
        this.partner.address.street = onboarding.address.street || "";
        this.partner.address.addressNumber = onboarding.address.addressNumber || "";
        this.partner.address.neighborhood = onboarding.address.neighborhood || "";
        this.partner.address.complement = onboarding.address.complement || "";
        this.partner.address.zipCode = onboarding.address.zipCode || "";
        this.partner.address.cityName = onboarding.address.cityName || "";
        this.partner.address.state = onboarding.address.state || "";
        this.partner.user = onboarding.users[0];
        this.partner.associates = onboarding.associates || [];
        this.partner.businessHours = onboarding.businessHours || [];
        this.partner.contractFile = onboarding.contractFile || "";
        this.partner.registrationStatusFile = onboarding.registrationStatusFile || "";
        this.partner.isPremium = onboarding.isPremium;
        this.partner.premiumSource = onboarding.premiumSource ?? null;
      } finally {
        this.loading = false;
      }
    },
    async handleAprove() {
      this.loading = true;
      try {
        await new UpdateOnboardingPartner().execute(this.$route.params.id, "ACTIVE");
        this.$store.dispatch("showSnack", {
          message: "Conta aprovada com sucesso",
          color: "green darken-1",
        });
        this.partner.status = "ACTIVE";
        this.handleBack();
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao aprovar a conta",
          color: "red",
        });
      } finally {
        this.loading = false;
      }
    },
    async handleReprove() {
      this.loading = true;
      try {
        await new UpdateOnboardingPartner().execute(this.$route.params.id, "PENDING");
        this.$store.dispatch("showSnack", {
          message: "Conta reprovada com sucesso",
          color: "green darken-1",
        });
        this.partner.status = "PENDING";
        this.handleBack();
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao reprovar a conta",
          color: "red",
        });
      } finally {
        this.loading = false;
      }
    },
    handleBack() {
      const status = this.$route.query.status;
      this.$router.push({
        path: "/partners",
        query: typeof status === "string" ? { status } : undefined,
      });
    },
    openWhatsApp(phone: string, name: string) {
      const phoneNumber = phone.replace(/\D/g, "");
      window.open(
        `https://wa.me/${phoneNumber}?text=Olá ${name}! Tudo bem? Somos da BRPoints.`,
        "_blank",
      );
    },
    formatPhone(value: string): string {
      if (!value) return "—";
      return `(${value.substr(0, 2)}) ${value.substr(2, 5)}-${value.substr(7, 8)}`;
    },
    formatDate(date: string): string {
      if (!date) return "—";
      return format(new Date(date), "dd/MM/yyyy HH:mm");
    },
    formatZipCod(cep: string): string {
      return cep ? cep.replace(/(\d{5})(\d{3})/, "$1-$2") : "—";
    },
    formatDocument(document: string): string {
      if (!document) return "—";
      if (document.length === 11) {
        return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (document.length === 14) {
        return document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      }
      return document;
    },
    handlePdfClick(pdfUrl: string) {
      this.clickedPdf = pdfUrl;
      this.showPdfDialog = true;
    },
    handlePushRouteQRCODE() {
      this.$router.push(
        `/partnerQRCODE/${this.partner.document}/${this.partner.name}/${this.partner.id}`,
      );
    },
  },
  mounted() {
    void this.handleLoadOnboarding();
  },
});
</script>

<style scoped lang="scss">
.entity-detail-page {
  max-width: 1280px;
}

.whatsapp-btn {
  background-color: #25d366 !important;
  color: white !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.premium-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  font-size: 13px;

  &--compact {
    padding: 2px 10px;
    font-size: 12px;
  }

  &--premium {
    background-color: #2e7d32;
  }

  &--voucher {
    background-color: #1565c0;
  }

  &--free {
    background-color: #c62828;
  }
}

.sidebar-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }
}

.sidebar-summary__label {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.sidebar-summary__value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f243f;
  text-align: right;
}

.business-hours-table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  overflow: hidden;
}

.business-hours-table__head,
.business-hours-table__row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  padding: 12px 16px;
  text-align: center;
  font-size: 0.9rem;
}

.business-hours-table__head {
  background: #f8fafc;
  font-weight: 700;
  color: #475569;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.business-hours-table__row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  color: #1f243f;

  &:last-child {
    border-bottom: none;
  }

  &:nth-child(even) {
    background: #fafbfc;
  }
}

.doc-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.doc-links__btn {
  text-transform: none;
  letter-spacing: 0;
}
</style>
